import { useEffect, useState, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useQueryClient } from '@tanstack/react-query';
import { Table } from 'antd';
import _ from 'lodash';
import AppButton from '../../../../components/ui/AppButton';
import { useGetShipping, useUpdateStateShipping } from '../../../../hooks/useShipping';
import { useAppStore } from '../../../../stores/appStore';
import { useNotificationStore } from '../../../../stores/notificationStore';
import './ORCDialog.css';

const components = {
  header: {
    cell: (props) => (
      <th {...props} style={{ fontSize: '1.25rem', backgroundColor: '#00475B', color: '#fff' }}>
        {props.children}
      </th>
    ),
  },
};

const ORCDialog = ({ open, onClose, OCRData }) => {
  const queryClient = useQueryClient();
  const setLoading = useAppStore((state) => state.setLoading);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [dataSource, setDataSource] = useState([]);
  const [isError, setIsError] = useState(false);
  const { mutateAsync: updateStateShipping } = useUpdateStateShipping();

  const { data: shippingData } = useGetShipping({
    page: 1,
    limit: 100,
    type_delivery: 'self delivery',
    tracking_url: '',
    enabled: open,
  });

  const shippingDataList = useMemo(() => {
    if (!shippingData) return [];

    return shippingData.Data.data.map((item) => ({
      name: item.address.name,
      shippingId: item._id,
      order_id: item.order_id,
      tracking_url: item.tracking_url,
      list_reward: item.list_reward,
    }));
  }, [shippingData]);

  const nameMap = useMemo(() => {
    const map = new Map();

    shippingDataList.forEach((item) => {
      if (!map.has(item.name)) {
        map.set(item.name, []);
      }

      map.get(item.name).push(item);
    });

    return map;
  }, [shippingDataList]);

  const uniqueNames = useMemo(() => Array.from(nameMap.keys()), [nameMap]);

  const handleInputChange = (key, field, value) => {
    setDataSource((prev) =>
      prev.map((item) => {
        if (item.key !== key) return item;

        if (field === 'name') {
          return {
            ...item,
            name: value,
            order_id: '',
            list_reward: [],
            errors: { ...item.errors, name: false },
            isError: false,
          };
        }

        if (field === 'order_id') {
          const matched = shippingDataList.find((s) => s.order_id === value);

          return {
            ...item,
            order_id: value,
            list_reward: matched?.list_reward || [],
            errors: { ...item.errors, order_id: false },
            isError: false,
          };
        }

        return {
          ...item,
          [field]: value,
          errors: { ...item.errors, [field]: false },
          isError: false,
        };
      })
    );
  };

  const handleValidate = () => {
    const validNameSet = new Set(shippingDataList.map((i) => i.name));
    const validOrderSet = new Set(shippingDataList.map((i) => i.order_id));

    const trackingCount = new Map();
    const orderCount = new Map();

    // first pass: count duplicates
    dataSource.forEach((item) => {
      if (item.tracking) {
        trackingCount.set(item.tracking, (trackingCount.get(item.tracking) || 0) + 1);
      }

      if (item.order_id) {
        orderCount.set(item.order_id, (orderCount.get(item.order_id) || 0) + 1);
      }
    });

    // second pass: validate
    const newData = dataSource.map((item) => {
      const errors = {
        express: !item.express,
        tracking: !item.tracking || trackingCount.get(item.tracking) > 1,
        name: !item.name || !validNameSet.has(item.name),
        order_id:
          !item.order_id || !validOrderSet.has(item.order_id) || orderCount.get(item.order_id) > 1,
      };

      return {
        ...item,
        errors,
        isError: Object.values(errors).some(Boolean),
      };
    });

    setDataSource(newData);

    return newData.some((item) => item.isError);
  };

  const handleValidateCount = () => {
    const hasCountError = dataSource.length > (shippingData?.Data.total || 0);
    setIsError(hasCountError);
    return hasCountError;
  };

  const mappingNewData = () =>
    dataSource.map((item) => {
      const matched = _.find(shippingDataList, { name: item.name, order_id: item.order_id });
      return { ...item, shippingId: matched?.shippingId || null };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasError = handleValidate();
    const hasCountError = handleValidateCount();

    if (hasError || hasCountError) return;
    const data = mappingNewData();

    try {
      setLoading(true);

      const results = await Promise.allSettled(
        data.map((item) =>
          updateStateShipping({
            params: { id: item.shippingId },
            dto: { tracking_no: item.tracking, express: item.express },
          })
        )
      );

      const rejected = results.filter((r) => r.status === 'rejected');
      const fulfilled = results.filter((r) => r.status === 'fulfilled');

      if (rejected.length > 0) {
        const rejectedOrderIds = new Set(rejected.map((r) => r.reason.response.data.Data.order_id));

        setDataSource((prev) =>
          prev.map((item) => {
            if (rejectedOrderIds.has(item.order_id)) {
              return {
                ...item,
                isError: true,
              };
            }
            return item;
          })
        );

        throw rejected[0].reason;
      }

      const responses = fulfilled.map((r) => r.value);

      if (responses.every((res) => res.Code === '20100')) {
        setNotification('แก้ไขสถานะการจัดส่งสำเร็จ', 'success');
        queryClient.invalidateQueries({ queryKey: ['shippingList'] });
        onClose();
      }
    } catch (error) {
      console.error(error);
      setNotification(error.response?.data.Message || 'บางรายการอัปเดตไม่สำเร็จ', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (OCRData && OCRData.length > 0) {
      setDataSource(
        OCRData.map((item, index) => ({
          ...item,
          key: index + 1,
          errors: {},
          isError: false,
        }))
      );
    }
  }, [OCRData]);

  const columns = [
    {
      title: 'Express',
      dataIndex: 'express',
      render: (text, record) => (
        <Box sx={{ width: '150px' }}>
          <Select
            value={text}
            onChange={(e) => handleInputChange(record.key, 'express', e.target.value)}
            displayEmpty
            error={record.errors?.express}
            sx={{
              width: '100%',
              borderRadius: '8px',
              backgroundColor: 'white',

              '& .MuiSelect-select': {
                fontSize: '1.25rem',
                lineHeight: 1.75,
                padding: '0 10px',
                height: '36px !important',
                color: text ? '#333' : '#aaa',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: record.errors?.express ? '2px solid #dc2626' : '2px solid #004B61',
              },
            }}
          >
            <MenuItem disabled value="">
              Express...
            </MenuItem>
            <MenuItem value="kerry express">Kerry Express</MenuItem>
            <MenuItem value="j&t express">J&amp;t Express</MenuItem>
            <MenuItem value="flash express">Flash Express</MenuItem>
            <MenuItem value="best express">Best Express</MenuItem>
            <MenuItem value="ไปรษณีย์ ems">ไปรษณีย์ EMS</MenuItem>
          </Select>
        </Box>
      ),
    },
    {
      title: 'Tracking No.',
      dataIndex: 'tracking',
      render: (text, record) => (
        <Box sx={{ width: '160px' }}>
          <TextField
            fullWidth
            value={text}
            onChange={(e) => handleInputChange(record.key, 'tracking', e.target.value)}
            error={record.errors?.tracking}
            placeholder="Tracking No."
            sx={{
              fontSize: '1.25rem',
              borderRadius: '8px',
              '& .MuiInputBase-root': {
                borderRadius: '8px',
                backgroundColor: 'white',
              },
              '& .MuiInputBase-input': {
                padding: '0 10px',
                height: '36px',
                color: '#333',
              },
              '& .MuiInputBase-input::placeholder': {
                fontSize: '1.25rem',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: '8px',
                border: '2px solid #004B61',
              },
            }}
          />
        </Box>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (text, record) => {
        return (
          <Box sx={{ width: '160px' }}>
            <Select
              value={text}
              onChange={(e) => handleInputChange(record.key, 'name', e.target.value)}
              displayEmpty
              error={record.errors?.name}
              sx={{
                width: '100%',
                borderRadius: '8px',
                backgroundColor: 'white',

                '& .MuiSelect-select': {
                  fontSize: '1.25rem',
                  padding: '0 10px',
                  height: '36px !important',
                  lineHeight: 1.75,
                  color: text ? '#333' : '#aaa',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: record.errors?.name ? '2px solid #dc2626' : '2px solid #004B61',
                },
              }}
            >
              <MenuItem disabled value="">
                Name...
              </MenuItem>
              {uniqueNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      },
    },
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      render: (text, record) => {
        return (
          <Box sx={{ width: '160px' }}>
            <Select
              value={text}
              onChange={(e) => handleInputChange(record.key, 'order_id', e.target.value)}
              displayEmpty
              error={record.errors?.name}
              sx={{
                width: '100%',
                borderRadius: '8px',
                backgroundColor: 'white',

                '& .MuiSelect-select': {
                  fontSize: '1.25rem',
                  padding: '0 10px',
                  height: '36px !important',
                  lineHeight: 1.75,
                  color: text ? '#333' : '#aaa',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: record.errors?.name ? '2px solid #dc2626' : '2px solid #004B61',
                },
              }}
            >
              <MenuItem disabled value="">
                Order ID...
              </MenuItem>
              {record.name &&
                nameMap.get(record.name)?.map((item) => (
                  <MenuItem key={item.order_id} value={item.order_id}>
                    {item.order_id}
                  </MenuItem>
                ))}
            </Select>
          </Box>
        );
      },
    },
    {
      title: 'Reward',
      dataIndex: 'list_reward',
      render: (text, record) => {
        if (!record?.list_reward || record?.list_reward?.length === 0) return '-';

        return (
          <Box sx={{ width: '160px' }}>
            <Typography sx={{ whiteSpace: 'pre-line' }}>
              {record?.list_reward
                ?.map((item) => `${item.name} x ${item.quantity} ${item.unit}\n`)
                .join('')}
            </Typography>
          </Box>
        );
      },
    },
    {
      title: 'Action',
      render: (text, record, index) => (
        <Box sx={{ width: '100px' }}>
          <IconButton
            aria-label="delete"
            onClick={() => {
              setDataSource((prev) => {
                const newData = prev.filter((_, idx) => idx !== index);
                setIsError(newData.length > (shippingData?.Data.total || 0));
                return newData;
              });
            }}
            sx={{
              borderRadius: '4px',
              backgroundColor: '#eee',
              fontSize: '1.25rem',

              '&:hover': {
                backgroundColor: '#eee',
                opacity: '0.85',
              },
            }}
          >
            <DeleteIcon sx={{ width: '20px', height: '20px', mr: 1 }} />
            Delete
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{ '& .MuiPaper-root': { maxWidth: '1000px' } }}
    >
      <DialogTitle id="alert-dialog-title" sx={{ fontSize: '2rem', fontWeight: '700' }}>
        Add Tracking
      </DialogTitle>
      <Box>
        {isError && (
          <Typography sx={{ color: '#ef4444', padding: '0 24px', fontSize: '1.25rem' }}>
            คุณอัพเดต Tracking No. เกินจำนวนในระบบ
          </Typography>
        )}
      </Box>
      <DialogContent sx={{ minWidth: '500px', minHeight: '150px' }}>
        <form onSubmit={handleSubmit}>
          <Table
            rowKey={(record) => record.key}
            components={components}
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            rowClassName={(record) => (record.isError ? 'highlight-row' : '')}
            rowHoverable={false}
            scroll={{ x: 'max-content' }}
          />

          <Box
            sx={{
              padding: '24px 0',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <AppButton
              variant="text"
              type="button"
              onClick={onClose}
              sx={{
                fontSize: '1.25rem',
                width: '120px',
                height: '40px',
                backgroundColor: '#e5e5e5',
                color: '#333',
                '&:hover': { opacity: '0.85', backgroundColor: '#e5e5e5' },
              }}
            >
              ยกเลิก
            </AppButton>
            <AppButton
              variant="text"
              type="submit"
              disabled={dataSource.length <= 0}
              sx={{
                fontSize: '1.25rem',
                width: '120px',
                height: '40px',
                backgroundColor: '#dc2626',
                color: 'white',

                '&:hover': { opacity: '0.85', backgroundColor: '#dc2626' },

                '&.Mui-disabled': {
                  backgroundColor: '#eee',
                },
              }}
            >
              ตกลง
            </AppButton>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ORCDialog;
